import React from 'react';
import { TourDefinition, TourStep } from './types';

/**
 * Yönetim panelinden (/admin/feature-tours) oluşturulan özel rehber satırını
 * TourDefinition'a çevirir. Adımlar: hedef CSS seçicisi, "önce tıkla" seçicisi
 * (ör. bir paneli açmak için), bekleme süresi, yerleşim, spot boşluğu.
 */
export interface CustomTourStepRow {
  id: string;
  title: string;
  content: string;
  target?: string;
  click_before?: string;
  wait_ms?: number;
  placement?: 'auto' | 'top' | 'bottom' | 'left' | 'right';
  spotlight_padding?: number;
  allow_interaction?: boolean;
}

export interface CustomTourRow {
  id: number;
  key: string;
  title: string;
  description?: string;
  page_path?: string;
  steps: CustomTourStepRow[];
  version: number;
  enabled: boolean;
  auto_start: boolean;
  audience: 'all' | 'users';
}

/**
 * "Önce tıkla": yalnızca click değil, gerçek bir dokunma gibi pointer dizisi gönderir
 * (framer-motion onTap gibi pointer tabanlı dinleyiciler de tetiklensin).
 */
export const simulateClick = (el: HTMLElement) => {
  const r = el.getBoundingClientRect();
  const base = { bubbles: true, cancelable: true, clientX: r.left + r.width / 2, clientY: r.top + r.height / 2, button: 0 };
  const fire = (type: string) => {
    try {
      el.dispatchEvent(new PointerEvent(type, { ...base, pointerId: 1, pointerType: 'mouse', isPrimary: true }));
    } catch {
      el.dispatchEvent(new MouseEvent(type, base));
    }
  };
  fire('pointerdown');
  fire('mousedown');
  fire('pointerup');
  fire('mouseup');
  el.click();
};

/** Düz metni satır sonlarıyla çizer */
export const renderText = (text: string): React.ReactNode => {
  const lines = String(text || '').split(/\r?\n/);
  return lines.map((line, i) => (
    <React.Fragment key={i}>
      {line}
      {i < lines.length - 1 && <br />}
    </React.Fragment>
  ));
};

export const customRowToTour = (r: CustomTourRow): TourDefinition => ({
  id: r.key || `custom-${r.id}`,
  version: Number(r.version) || 1,
  title: r.title,
  autoStart: r.auto_start !== false,
  autoStartDelayMs: 2500,
  pagePath: r.page_path || undefined,
  custom: true,
  steps: (r.steps || []).map(
    (st, i): TourStep => ({
      id: st.id || `s${i + 1}`,
      target: st.target || undefined,
      title: st.title,
      content: renderText(st.content),
      placement: st.placement || 'auto',
      spotlightPadding: typeof st.spotlight_padding === 'number' ? st.spotlight_padding : 8,
      allowInteraction: !!st.allow_interaction,
      onBefore: async (ctx) => {
        if (st.click_before) {
          const el = document.querySelector(st.click_before) as HTMLElement | null;
          if (el) simulateClick(el);
        }
        if (st.wait_ms || st.click_before) await ctx.sleep(st.wait_ms || 500);
      },
    }),
  ),
});
